import { seededRandom } from "./seeded-random";

export class LSystem {
    private _generations = new Map<number, string>();
    private _rand!: () => number;

    constructor(
        public readonly options: {
            start: string;
            rules: Record<string, string | { rule: string; prob: number }[]>;
            seed: string;
            maxGenerations: number;
        },
    ) {
        this.destroy();
    }

    get rand() {
        return this._rand;
    }

    get maxGenerations() {
        return this.options.maxGenerations;
    }

    public count() {
        return [...this._generations.keys()].length;
    }

    public iteration(n?: number): string | null {
        if (n === undefined) {
            const lastN = Math.max(...this._generations.keys());
            return this._generations.get(lastN) || null;
        }
        if (n >= this.options.maxGenerations) {
            return null;
        }

        if (this._generations.get(n) !== undefined) {
            return this._generations.get(n)!;
        }

        let lastGen: string | undefined;
        for (let gen = 0; gen <= n; gen++) {
            if (!this._generations.has(gen) && lastGen) {
                this._generations.set(gen, this.iterate(lastGen));
            }
            lastGen = this._generations.get(gen);
        }

        return this._generations.get(n) || null;
    }

    private iterate(lastGen: string) {
        let next = "";

        for (let i = 0; i < lastGen.length; i++) {
            const c = lastGen[i];
            if (c in this.options.rules) {
                const rule = this.options.rules[c];
                if (Array.isArray(rule)) {
                    next += this._chooseRule(rule);
                } else {
                    next += this.options.rules[c];
                }
            } else {
                next += c;
            }
        }

        return next;
    }

    public destroy() {
        this._initGenerations();
        this._initRand();
    }

    private _initRand() {
        this._rand = seededRandom(this.options.seed);
    }

    private _initGenerations() {
        this._generations.clear();
        this._generations.set(0, this.options.start);
    }

    private _chooseRule(ruleSet: { rule: string; prob: number }[]) {
        let total = 0;
        const rand = this._rand();
        for (const rule of ruleSet) {
            total += rule.prob;
            if (total > rand) {
                return rule.rule;
            }
        }
        return "";
    }
}
